import { useCallback, useEffect, useRef, useState } from "react";

import { useFieldErrors } from "./use-field-errors";
import type { FieldErrors } from "./use-field-errors";

export interface UseDialogFormOptions<V extends Record<string, unknown>> {
  initial: V | (() => V);
  /** Erros por campo; objeto vazio (ou sem chaves com mensagem) significa válido. */
  validate?: (values: V) => Partial<Record<keyof V, string>>;
  onSubmit: (values: V) => Promise<void> | void;
  /** Volta ao `initial` quando `isOpen` passa de `true` para `false`. Padrão: `true`. Sem `isOpen`, não faz nada. */
  resetOnClose?: boolean;
  /** Estado de abertura do dialog (ex.: `useDialogState().isOpen`), usado por `resetOnClose`. */
  isOpen?: boolean;
}

export interface UseDialogFormResult<V extends Record<string, unknown>> {
  values: V;
  setValue: <K extends keyof V>(key: K, value: V[K]) => void;
  setValues: (values: V) => void;
  errors: FieldErrors<Extract<keyof V, string>>;
  hasErrors: boolean;
  /** `true` quando `values` difere do `initial`. */
  isDirty: boolean;
  isSubmitting: boolean;
  /** Mensagem do erro lançado por `onSubmit`; `null` fora disso. */
  submitError: string | null;
  /** Valida primeiro; só chama `onSubmit` sem erros. Devolve `true` se enviou com sucesso; nunca lança. */
  submit: () => Promise<boolean>;
  /** Volta ao `initial`; com `next`, rebaseia o `initial` para `next` (ex.: abrir um dialog de edição). */
  reset: (next?: V) => void;
}

function sameValues(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function errorMessage(error: unknown): string {
  return error instanceof Error && error.message
    ? error.message
    : "Não foi possível concluir a operação.";
}

/** Formulário de dialog: valores, validação em tela, envio e estado de submissão. */
export function useDialogForm<V extends Record<string, unknown>>(
  options: UseDialogFormOptions<V>,
): UseDialogFormResult<V> {
  type Key = Extract<keyof V, string>;
  const latest = useRef(options);
  latest.current = options;

  const resolveInitial = useCallback((): V => {
    const { initial } = latest.current;
    return initial instanceof Function ? initial() : initial;
  }, []);

  const [initialValues] = useState<V>(resolveInitial);
  const initialRef = useRef<V>(initialValues);
  /** Depois de `reset(next)`, o `reset()` sem argumento volta a `next` em vez de reavaliar o `initial`. */
  const rebased = useRef(false);

  const [values, setValuesState] = useState<V>(initialValues);
  const valuesRef = useRef(values);
  const fieldErrors = useFieldErrors<Key>();
  const { clearError, clearAll, setError } = fieldErrors;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const setValues = useCallback((next: V) => {
    valuesRef.current = next;
    setValuesState(next);
  }, []);

  const setValue = useCallback(
    <K extends keyof V>(key: K, value: V[K]) => {
      setValues({ ...valuesRef.current, [key]: value });
      clearError(key as unknown as Key);
    },
    [setValues, clearError],
  );

  const reset = useCallback(
    (next?: V) => {
      if (next) rebased.current = true;
      initialRef.current = next ?? (rebased.current ? initialRef.current : resolveInitial());
      setValues(initialRef.current);
      clearAll();
      setSubmitError(null);
    },
    [resolveInitial, setValues, clearAll],
  );

  const submittingRef = useRef(false);

  const submit = useCallback(async () => {
    // O estado `isSubmitting` só muda no próximo render: o ref barra o segundo submit do mesmo tick.
    if (submittingRef.current) return false;
    const { validate, onSubmit } = latest.current;
    let found: Partial<Record<keyof V, string>>;
    try {
      found = validate?.(valuesRef.current) ?? {};
    } catch (error) {
      // `submit` nunca lança: erro na validação vira `submitError`.
      clearAll();
      setSubmitError(errorMessage(error));
      return false;
    }
    const keys = (Object.keys(found) as Key[]).filter((key) => found[key]);
    clearAll();
    if (keys.length > 0) {
      setSubmitError(null);
      for (const key of keys) setError(key, found[key] as string);
      return false;
    }

    setSubmitError(null);
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      await onSubmit(valuesRef.current);
      return true;
    } catch (error) {
      setSubmitError(errorMessage(error));
      return false;
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [clearAll, setError]);

  const wasOpen = useRef(options.isOpen ?? false);
  useEffect(() => {
    const isOpen = options.isOpen ?? false;
    if (wasOpen.current && !isOpen && (latest.current.resetOnClose ?? true)) {
      // Fechar volta ao `initial` da opção, não ao rebaseado por `reset(next)`.
      rebased.current = false;
      reset();
    }
    wasOpen.current = isOpen;
  }, [options.isOpen, reset]);

  return {
    values,
    setValue,
    setValues,
    errors: fieldErrors.errors,
    hasErrors: fieldErrors.hasErrors,
    isDirty: !sameValues(values, initialRef.current),
    isSubmitting,
    submitError,
    submit,
    reset,
  };
}
